import {useContext, useId} from 'react';

import {cn} from '../../lib/utils';
import {Icon} from '../Icon/Icon';
import {useDirection} from '../shadcn/direction';
import {
  Sidebar as SidebarPrimitive,
  SidebarContext,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarProvider,
  SidebarTrigger
} from '../shadcn/sidebar';

import type {IconName} from '../../icons/icon-map';
import type {ReactNode} from 'react';

export interface SidebarItemBadge {
  count: number;
  variant?: 'error' | 'warning' | 'info';
  label?: string;
}

export interface SidebarItem {
  id: string;
  label: string;
  icon: IconName;
  isActive?: boolean;
  disabled?: boolean;
  badges?: SidebarItemBadge[];
  onClick?: () => void;
}

export interface SidebarNavGroup {
  id: string;
  label?: string;
  items: SidebarItem[];
}

export interface SidebarProps {
  items?: SidebarItem[];
  groups?: SidebarNavGroup[];
  logo: ReactNode;
  collapsedLogo?: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  footer?: ReactNode;
  children?: ReactNode;
  /**
   * `floating` (default) — rounded, inset main content with a shadow gap, matching apps/web.
   * `flush` — sidebar and main content sit edge-to-edge, separated by a plain full-height
   * border instead of the rounded/shadowed card look.
   */
  variant?: 'floating' | 'flush';
  /** Shows placeholder nav rows in place of `items`/`groups`. */
  isLoading?: boolean;
}

const MAX_BADGE_COUNT = 99;
const LOADING_ROW_COUNT = 6;

const badgeVariantClass: Record<NonNullable<SidebarItemBadge['variant']>, string> = {
  error: 'bg-indication-error text-fg-on-indication',
  warning: 'bg-indication-warning text-fg-on-indication',
  info: 'bg-indication-info text-fg-on-indication'
};

function renderItems(items: SidebarItem[], idPrefix: string) {
  return items.map(item => {
    const badges = item.badges?.filter(badge => badge.count > 0) ?? [];
    const badgesId = `${idPrefix}-${item.id}-badges`;
    return (
      <SidebarMenuItem key={item.id}>
        <SidebarMenuButton
          tooltip={item.label}
          isActive={item.isActive}
          disabled={item.disabled}
          onClick={item.disabled ? undefined : item.onClick}
          aria-describedby={badges.length > 0 ? badgesId : undefined}
        >
          <Icon name={item.icon} className="size-4" />
          <span>{item.label}</span>
        </SidebarMenuButton>
        {badges.length > 0 && (
          <SidebarMenuBadge id={badgesId} className="gap-1 px-0">
            {badges.map((badge, index) => (
              <span
                key={`${badge.variant ?? 'default'}-${index}`}
                data-slot="badge"
                className={cn(
                  'flex h-5 min-w-5 items-center justify-center rounded-md px-1',
                  badge.variant && badgeVariantClass[badge.variant]
                )}
              >
                {badge.label && <span className="sr-only">{badge.label} </span>}
                {badge.count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : badge.count}
              </span>
            ))}
          </SidebarMenuBadge>
        )}
      </SidebarMenuItem>
    );
  });
}

function renderNav(
  groups: SidebarNavGroup[] | undefined,
  items: SidebarItem[] | undefined,
  idPrefix: string
) {
  if (groups) {
    return groups.map(group => (
      <SidebarGroup key={group.id}>
        {group.label && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
        <SidebarMenu>{renderItems(group.items, idPrefix)}</SidebarMenu>
      </SidebarGroup>
    ));
  }
  return (
    items && (
      <SidebarGroup>
        <SidebarMenu>{renderItems(items, idPrefix)}</SidebarMenu>
      </SidebarGroup>
    )
  );
}

export function Sidebar({
  items,
  groups,
  logo,
  collapsedLogo,
  open,
  defaultOpen = true,
  onOpenChange,
  footer,
  children,
  variant = 'floating',
  isLoading = false
}: SidebarProps) {
  const direction = useDirection();
  const side = direction === 'rtl' ? 'right' : 'left';
  const idPrefix = useId();
  return (
    <SidebarProvider
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      dir={direction}
    >
      <SidebarPrimitive
        collapsible="icon"
        variant={variant === 'flush' ? 'sidebar' : 'inset'}
        side={side}
      >
        <SidebarHeader
          className="ms-3 mt-3 flex flex-row items-center justify-between gap-2 overflow-hidden
            group-data-[collapsible=icon]:ms-0
            group-data-[collapsible=icon]:w-full"
        >
          <div className="flex-1 overflow-hidden transition-opacity duration-300 ease-in-out group-data-[collapsible=icon]:hidden group-data-[collapsible=icon]:opacity-0">
            {logo}
          </div>
          <SidebarTrigger className="shrink-0" />
        </SidebarHeader>
        {collapsedLogo && (
          <div className="hidden items-center justify-center py-2 opacity-0 transition-opacity duration-300 ease-in-out group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:opacity-100">
            {collapsedLogo}
          </div>
        )}
        <SidebarContent>
          {isLoading ? <SidebarLoadingRows /> : renderNav(groups, items, idPrefix)}
        </SidebarContent>
        {footer && <SidebarFooter>{footer}</SidebarFooter>}
      </SidebarPrimitive>
      {children}
    </SidebarProvider>
  );
}

export function useSidebarOpen(): boolean | null {
  return useContext(SidebarContext)?.open ?? null;
}

function SidebarLoadingRows() {
  return (
    <SidebarGroup aria-hidden>
      <SidebarMenu>
        {Array.from({length: LOADING_ROW_COUNT}, (_, index) => (
          <SidebarMenuSkeleton key={index} showIcon className="h-10.5 gap-3 px-3" />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
